import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Subject } from 'rxjs';
import {
  CookieSettings,
  defaultAcceptedCookieSettings,
  defaultCookieSettings,
  defaultRejectedCookieSettings,
} from './cookie-settings/cookie.types';

@Injectable({ providedIn: 'root' })
export class CookiesService {
  domainName =
    window.location.href.indexOf('localhost') !== -1
      ? 'localhost'
      : 'geneplanet.com';

  cookiesAccepted$: Subject<boolean> = new Subject<boolean>();
  LScookiesSettings = '@geneplanet/cookie_settings';

  private readonly cPref = 'cookies_pref';
  private readonly cStat = 'cookies_stat';
  private readonly cMkt = 'cookies_mkt';
  private readonly cLang = 'pref_lang';
  private readonly cAff = 'cookies_aff';
  private readonly cConf = 'cookies_configured';

  // eslint-disable-next-line @typescript-eslint/member-ordering
  listOfCookies = [this.cPref, this.cStat, this.cMkt];

  constructor(
    private cookieService: CookieService // private analytics: AnalyticsService
  ) {}

  areCookiesSet(): boolean {
    return this.listOfCookies.every((c) => {
      return this.cookieService.get(c) ? true : false;
    });
  }

  saveCookies(cookieSettings: CookieSettings, lang?: string) {
    if (
      this.saveCookieSettingsToLocalStorage(true) &&
      this.saveCookieSettingsToCookies(cookieSettings, true)
    ) {
      // this.analytics.trackPageViewEvent();
      if (lang && cookieSettings.cookies_pref === 1) {
        this.saveLanguagePreference(lang);
      }
      this.cookiesAccepted$.next(true);
    }
  }

  acceptAllCookies() {
    const currentCookieSettings = this.getCookieSettings();
    const allAccepted = this.listOfCookies.every((cookie) => {
      return currentCookieSettings[cookie] === 1;
    });
    if (!allAccepted) {
      this.saveCookieSettingsToCookies(defaultAcceptedCookieSettings, true);
    }
    this.saveConfiguredCookie(true);
    this.cookiesAccepted$.next(true);
  }

  checkIfAtLeastOneAccepted(): boolean {
    const currentCookieSettings = this.getCookieSettings();
    return this.listOfCookies.some((cookie) => {
      return currentCookieSettings[cookie] === 1;
    });
  }

  saveCookiesFirstPageView() {
    this.saveCookieSettingsToCookies(defaultRejectedCookieSettings, false);
  }

  getCookieSettings(): CookieSettings {
    const cookieSettings: CookieSettings = defaultCookieSettings;
    this.listOfCookies.forEach((cookie) => {
      const cookieValue = this.cookieService.get(cookie);
      cookieSettings[cookie] = cookieValue ? Number(cookieValue) : 0;
    });
    return cookieSettings;
  }

  saveAffiliateCookie(code: string) {
    if (!this.checkIfAffiliateCookieExists()) {
      const expirationDate = new Date(
        new Date().setFullYear(new Date().getFullYear() + 1)
      );
      this.cookieService.set(
        this.cAff,
        code,
        expirationDate,
        '/',
        this.domainName
      );
    }
  }

  saveLanguagePreference(lang: string) {
    const cookieSettings = this.getCookieSettings();
    if (cookieSettings.cookies_pref && cookieSettings.cookies_pref === 1) {
      const expirationDate = new Date(
        new Date().setFullYear(new Date().getFullYear() + 1)
      );
      this.cookieService.set(
        this.cLang,
        lang,
        expirationDate,
        '/',
        this.domainName
      );
    }
  }

  saveConfiguredCookie(allAcepted: boolean) {
    if (!this.checkIfConfiguredCookieExists()) {
      const expirationDate = allAcepted
        ? new Date(new Date().setFullYear(new Date().getFullYear() + 2))
        : new Date(new Date().setDate(new Date().getDate() + 14));
      this.cookieService.set(
        this.cConf,
        '1',
        expirationDate,
        '/',
        this.domainName
      );
    }
  }

  getLanguagePreference(): string {
    return this.cookieService.get(this.cLang);
  }

  checkIfConfiguredCookieExists(): boolean {
    return !!this.cookieService.get(this.cConf);
  }

  private checkIfAffiliateCookieExists(): boolean {
    return this.cookieService.get(this.cAff) ? true : false;
  }

  private saveCookieSettingsToLocalStorage(configured: boolean): boolean {
    window.localStorage.setItem(
      this.LScookiesSettings,
      JSON.stringify({ configured })
    );
    return true;
  }

  private saveCookieSettingsToCookies(cookieSettings, first = false): boolean {
    const expirationDate = new Date(
      new Date().setFullYear(new Date().getFullYear() + 2)
    );
    this.listOfCookies.forEach((cookie) => {
      this.cookieService.set(
        cookie,
        cookieSettings[cookie],
        expirationDate,
        '/',
        this.domainName
      );
    });
    this.saveCookieSettingsToLocalStorage(first);
    return true;
  }
}
